import { useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../lib/api";
import { productOf, useNotifications } from "../lib/notifications";
import { queryKeys } from "../lib/queryKeys";
import { absoluteTime, relativeTime } from "../lib/time";
import { cn } from "../components/ui/cn";
import type { AppNotification, NotificationProduct } from "../types";

/**
 * Everything anybody has told you, kept.
 *
 * The bell answers "what have I missed" -- a glance, the newest fifty, gone
 * from mind once read. It is the wrong shape for the other question people
 * actually have, which is "when was I told about that, and what did it say",
 * asked days later about something that was read in the bell and then scrolled
 * out of it. That question needs a page you can scroll and filter,
 * so this is one.
 *
 * Tabbed by product, with All first, because the four things this desk does
 * produce very different notifications and somebody chasing a CRM handover is
 * not helped by three hundred ticket assignments in between. Filtering happens
 * on the server so a tab is a page of its own product rather than a page of
 * everything with most of it hidden -- which on a quiet product would show two
 * rows and claim that was all of them.
 *
 * Reading a row here marks it read, exactly as the bell does. There is one
 * unread state and it belongs to the notification, not to the surface it
 * happened to be seen on.
 */

const TABS: { id: NotificationProduct | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "helpdesk", label: "Helpdesk" },
  { id: "crm", label: "CRM" },
  { id: "sales", label: "Sales calls" },
  { id: "meetings", label: "Meetings" },
];

/**
 * The dot that says where a row came from.
 *
 * The same four colours the bell uses. Two legends for one set of sources
 * would be a thing to learn twice.
 */
const DOT: Record<NotificationProduct, string> = {
  helpdesk: "bg-sla-ok",
  crm: "bg-sla-warning",
  sales: "bg-brand-600",
  meetings: "bg-brand-400",
};

export function Notifications() {
  const [tab, setTab] = useState<NotificationProduct | "all">("all");
  const queryClient = useQueryClient();

  /* The bell's own store, so marking something read here moves the count in
     the corner rather than leaving two numbers disagreeing. */
  const { markAllRead, unreadCount } = useNotifications();

  const product = tab === "all" ? undefined : tab;

  const history = useInfiniteQuery({
    queryKey: queryKeys.notificationHistory(product ?? null),
    queryFn: ({ pageParam }) =>
      api.notificationHistory({ product, before: pageParam }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 30_000,
  });

  const items = history.data?.pages.flatMap((page) => page.items) ?? [];

  /**
   * Mark one row read without refetching the page under the reader.
   *
   * Patched in place across every cached tab, because the same notification
   * appears in All and in its own product's tab -- reading it in one and
   * finding it still bold in the other is the kind of small wrongness that
   * makes people stop trusting the count.
   */
  const markRead = (id: number): void => {
    void api.markNotificationsRead([id]);

    queryClient.setQueriesData<{
      pages: { items: AppNotification[]; nextCursor: number | null }[];
      pageParams: unknown[];
    }>({ queryKey: queryKeys.notificationHistoryRoot }, (previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        pages: previous.pages.map((page) => ({
          ...page,
          items: page.items.map((entry) =>
            entry.id === id ? { ...entry, read: true } : entry,
          ),
        })),
      };
    });

    /* `exact`, because the history keys sit under the bell's own prefix and a
       loose invalidation would refetch the very pages just patched -- undoing
       the patch and jumping the reader back to the top. */
    void queryClient.invalidateQueries({
      queryKey: queryKeys.notifications,
      exact: true,
    });
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl px-4 pb-10 pt-4 sm:px-6">
        <header className="mb-3 flex flex-wrap items-center gap-2">
          <h1 className="text-base font-semibold text-content">
            Notifications
          </h1>
          {unreadCount > 0 ? (
            <span className="rounded-full bg-brand-600 px-2 py-0.5 text-[11px] font-medium text-white">
              {unreadCount} unread
            </span>
          ) : null}

          {unreadCount > 0 ? (
            <button
              type="button"
              onClick={() => markAllRead()}
              className="ml-auto text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
            >
              Mark all as read
            </button>
          ) : null}
        </header>

        <div className="mb-3 flex flex-wrap gap-1" role="tablist">
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={tab === entry.id}
              onClick={() => setTab(entry.id)}
              className={cn(
                "rounded-md px-2.5 py-1 text-xs transition",
                tab === entry.id
                  ? "bg-brand-600 text-white"
                  : "text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
              )}
            >
              {entry.label}
            </button>
          ))}
        </div>

        {history.isLoading ? (
          <p className="py-12 text-center text-xs text-faint">Loading…</p>
        ) : items.length === 0 ? (
          <p className="rounded-lg border border-dashed border-line py-12 text-center text-xs text-faint">
            {tab === "all"
              ? "Nothing has been sent to you yet."
              : "Nothing from here yet."}
          </p>
        ) : (
          <ul className="overflow-hidden rounded-lg border border-line">
            {items.map((entry) => (
              <Row key={entry.id} entry={entry} onRead={markRead} />
            ))}
          </ul>
        )}

        {history.hasNextPage ? (
          <button
            type="button"
            onClick={() => void history.fetchNextPage()}
            disabled={history.isFetchingNextPage}
            className="mt-3 w-full rounded-md border border-line py-2 text-xs text-muted hover:bg-surface-2 hover:text-brand-600 disabled:opacity-50 dark:hover:text-brand-400"
          >
            {history.isFetchingNextPage ? "Loading…" : "Show older"}
          </button>
        ) : items.length > 0 ? (
          /* Said out loud, because a list that simply stops looks truncated.
             Ninety days is the retention on read notifications -- see
             RETENTION_DAYS on the server. */
          <p className="mt-3 text-center text-[11px] text-faint">
            That is everything from the last 90 days.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function Row({
  entry,
  onRead,
}: {
  entry: AppNotification;
  onRead: (id: number) => void;
}) {
  const product = productOf(entry.eventKey);

  return (
    <li className="border-b border-line last:border-0 odd:bg-surface even:bg-app">
      <Link
        to={entry.href}
        onClick={() => {
          if (!entry.read) onRead(entry.id);
        }}
        className="flex items-start gap-2.5 px-3 py-2.5 hover:bg-surface-2"
      >
        {/* The dot carries two things at once: which product, by colour, and
            whether it has been read, by being filled or hollow. */}
        <span
          aria-hidden
          className={cn(
            "mt-1.5 size-2 shrink-0 rounded-full",
            entry.read ? "bg-transparent ring-1 ring-line" : DOT[product],
          )}
        />

        <span className="min-w-0 flex-1">
          <span
            className={cn(
              "block text-xs",
              entry.read ? "text-muted" : "font-medium text-content",
            )}
          >
            {entry.title}
          </span>
          {entry.body ? (
            <span className="block truncate text-[11px] text-faint">
              {entry.body}
            </span>
          ) : null}
        </span>

        <time
          dateTime={entry.at}
          title={absoluteTime(entry.at)}
          className="shrink-0 text-[11px] text-faint"
        >
          {relativeTime(entry.at)}
        </time>
      </Link>
    </li>
  );
}
